import { useMemo, type FC } from 'react';
import { UiHStack, UiStack, uiStyles, UiText } from '@/lib/ui';
import Item from './Item';
import { useCartQuery } from '@/registration/hook/useCartQuery';
import QueryContainer, { StandardSpinner } from '@/base/QueryContainer/QueryContainer';
import { useRegistrationQuery } from '@/registration/hook/useRegistrationQuery';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import BaseMessageBarInfo from '@/base/MessageBar/Info';
import { useEventSettingsQuery } from '@/registration/hook/useEventSettingsQuery';
import { useShoppingCart } from '@/registration/hook/useShoppingCart';

export interface CartProps {
  showErrors?: boolean
}

const Cart: FC<CartProps> = ({ showErrors }) => {
  const { eventId } = useRegisterRoute();
  const { data: eventSettings } = useEventSettingsQuery(eventId);
  const registrationQuery = useRegistrationQuery(eventId);
  const cartQuery = useCartQuery(eventId);

  const subchargeLabel = useMemo(
    () => { return eventSettings?.surcharge?.label ?? 'Processing fee'; },
    [eventSettings?.surcharge]
  );

  return (
    <QueryContainer
      query={registrationQuery}
      loadingComponent={<StandardSpinner />}
      placeHolder={(
        <UiStack>
          <BaseMessageBarInfo borderRadius={uiStyles.borderRadius} bgColor={'#fff'}>
            Your cart is empty.
          </BaseMessageBarInfo>
        </UiStack>
      )}
    >
      {(registration) => {
        return (
          <QueryContainer query={cartQuery} errorMessage="Failed to load the shopping cart.">
            {(shoppingCart) => {
              return (
                (!shoppingCart?.cartItems || shoppingCart.cartItems.length === 0) ? (
                  <UiStack>
                    <BaseMessageBarInfo borderRadius={uiStyles.borderRadius} bgColor={'#fff'}>
                      Your cart is empty.
                    </BaseMessageBarInfo>
                  </UiStack>
                ) : (
                  <UiStack flexGrow={1} spacing={4}>
                    {/*<UiText variant={'title'}>Shopping cart</UiText>*/}
                    {shoppingCart.cartItems.map((itemData, index) => {
                      return (
                        <Item
                          key={index}
                          itemId={itemData.id}
                          name={itemData.name}
                          quantity={itemData.quantity}
                          priceFormat={itemData.priceFormat}
                          cartId={shoppingCart.id}
                          registrationId={registration.id}
                          errors={showErrors === true ? itemData.errors : undefined}
                        />
                      );
                    })}
                    <UiHStack justifyContent={'flex-end'} flexGrow={1}>
                      <UiStack py={2} justifyContent={'flex-end'} px={4}>
                        <UiHStack alignItems={'center'} justifyContent={'flex-end'}>
                          <UiText variant={'body1'} color={'text.secondary'}>Subtotal</UiText>
                          <UiText variant={'body1'}>{cartQuery.data?.subtotalFormat ?? 0}</UiText>
                        </UiHStack>
                        {shoppingCart.tax?.map((taxItem, index) => {
                          return (
                            <UiHStack alignItems={'center'} justifyContent={'flex-end'} key={index}>
                              <UiText variant={'body1'} color={'text.secondary'}>{taxItem.label}</UiText>
                              <UiText variant={'body1'}>{taxItem.amountFormat ?? 0}</UiText>
                            </UiHStack>
                          );
                        })}
                        {(!!shoppingCart.surchargeFormat) && (
                          <UiHStack alignItems={'center'} justifyContent={'flex-end'}>
                            <UiText variant={'body1'} color={'text.secondary'}>{subchargeLabel}</UiText>
                            <UiText variant={'body1'}>{shoppingCart.surchargeFormat}</UiText>
                          </UiHStack>
                        )}

                        <UiHStack alignItems={'center'} justifyContent={'flex-end'}>
                          <UiText variant={'body1'} color={'text.secondary'}>Total amount</UiText>
                          <UiText variant={'title'}>{cartQuery.data?.totalAmount ?? 0}</UiText>
                        </UiHStack>
                      </UiStack>
                    </UiHStack>
                  </UiStack>
                )
              );
            }}
          </QueryContainer>
        );
      }}
    </QueryContainer>
  );
};

export default Cart;
